import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Briefcase, TrendingUp, PieChart, BookOpen, MapPin, UserCheck, CalendarCheck, Phone, Star, ArrowRight, CheckCircle2 } from "lucide-react";
import { SERVICES } from "../data/services";
import { CONTACT } from "../components/Footer";
import SEO, { LOCAL_BUSINESS_LD } from "../components/SEO";

const ICONS = { "individual-tax-returns": FileText, "small-business-accounting": Briefcase, "tax-planning": TrendingUp, "financial-consulting": PieChart, "bookkeeping": BookOpen };

const TRUST_BADGES = [
  { icon: MapPin, label: "Tampa Local" },
  { icon: UserCheck, label: "Personal, One-on-One Service" },
  { icon: CheckCircle2, label: "Accuracy You Can Trust" },
  { icon: CalendarCheck, label: "Free Initial Consultation" },
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }) };

export default function Home({ onStartTax, onBookAppointment }) {
  return (
    <div data-testid="home-page">
      <SEO path="/" jsonLd={LOCAL_BUSINESS_LD}
        title="Marizema Tax Service | Tax Preparation & Bookkeeping — Tampa, FL"
        description="Professional tax preparation, bookkeeping and financial services for individuals and small businesses in Tampa, Florida. Call (813) 728-0702." />

      <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-[#0B2545]">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1688250282575-7147f8282561?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Tampa skyline" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B2545] via-[#0B2545]/90 to-[#0B2545]/45" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-36 w-full">
          <motion.div initial="hidden" animate="show" className="max-w-2xl">
            <motion.p variants={fadeUp} custom={0} className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D4AF37] mb-4">
              Marizema Tax Service LLC — Tampa, Florida
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
              Professional tax and bookkeeping solutions for individuals and small businesses.
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">
              Accurate, reliable, and tailored to your needs — with personal attention from a local Florida team that treats your finances like their own.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="mt-8 flex flex-col sm:flex-row gap-3">
              <button data-testid="hero-cta-start-tax" onClick={onStartTax}
                className="px-7 py-3.5 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors shadow-lg shadow-black/30">
                Start Tax Return
              </button>
              <button data-testid="hero-cta-book-appointment" onClick={onBookAppointment}
                className="px-7 py-3.5 rounded-full border border-[#C9A227]/70 text-[#D4AF37] text-sm font-bold hover:bg-[#C9A227]/10 transition-colors">
                Book Appointment
              </button>
            </motion.div>
            <motion.p variants={fadeUp} custom={3} className="mt-4 text-sm text-slate-300">
              Or call us: <a data-testid="hero-phone-link" href={CONTACT.phoneTel} className="font-semibold text-[#D4AF37] hover:text-white underline underline-offset-2">{CONTACT.phoneDisplay}</a>
            </motion.p>
            <motion.div variants={fadeUp} custom={4} className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {TRUST_BADGES.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2.5">
                  <Icon size={20} className="text-[#D4AF37] shrink-0" />
                  <span className="text-xs font-medium text-slate-100 leading-tight">{label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-widest font-semibold text-[#86680A] mb-3">Our Services</p>
            <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B2545]">
              A wide array of services tailored to your unique tax needs
            </h2>
            <p className="mt-4 text-[#1F2933]/80 leading-relaxed">
              Whether you're an individual seeking help with your annual tax return or a small business owner in need of comprehensive accounting support, we've got you covered.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.slug];
              return (
                <motion.div key={s.slug} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} custom={i}>
                  <Link to={s.dedicated ? "/marizema-bookkeeping-service" : "/our-services"} data-testid={`home-service-card-${s.slug}`}
                    className="group block bg-white border border-slate-200 hover:border-[#C9A227]/60 rounded-2xl p-6 sm:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-300/60 h-full">
                    <div className="w-11 h-11 rounded-xl bg-[#0B2545] flex items-center justify-center mb-5">
                      <Icon size={22} className="text-[#D4AF37]" />
                    </div>
                    <h3 className="font-serif-display text-lg sm:text-xl font-semibold text-[#0B2545] group-hover:text-[#86680A] transition-colors">{s.name}</h3>
                    <p className="mt-2.5 text-sm text-[#1F2933]/75 leading-relaxed">{s.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#86680A]">
                      Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#14375E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-widest font-semibold text-[#D4AF37] mb-6">What Our Clients Say</p>
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => <Star key={i} size={20} className="text-[#D4AF37] fill-[#D4AF37]" />)}
          </div>
          <blockquote data-testid="home-testimonial" className="font-serif-display text-xl sm:text-2xl lg:text-3xl text-white leading-relaxed italic">
            "I highly recommend Marizema Tax Service LLC to anyone in need of tax assistance. Their team is knowledgeable, friendly, and truly cares about their clients."
          </blockquote>
          <p className="mt-6 text-sm font-semibold text-[#D4AF37]">Marshet M.</p>
          <p className="text-xs text-slate-300 mt-1">Marizema Tax Service Client</p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0B2545] border border-[#C9A227]/30 rounded-3xl p-8 sm:p-14 text-center">
            <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white">
              Ready to make tax season stress-free?
            </h2>
            <p className="mt-4 text-slate-300 max-w-xl mx-auto">
              Start your tax return online in minutes, or book a free consultation with our Tampa team today.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
              <button data-testid="home-cta-start-tax" onClick={onStartTax}
                className="px-7 py-3.5 rounded-full bg-[#C9A227] text-[#0B2545] text-sm font-bold hover:bg-[#D4AF37] transition-colors">
                Start Tax Return
              </button>
              <button data-testid="home-cta-book-appointment" onClick={onBookAppointment}
                className="px-7 py-3.5 rounded-full border border-[#C9A227]/70 text-[#D4AF37] text-sm font-bold hover:bg-[#C9A227]/10 transition-colors">
                Book Appointment
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
